export type GlobeCoordinate = [number, number];

const EARTH_RADIUS_METERS = 6378137;
const HORIZON_EPSILON = 1e-10;
const INTERSECTION_ITERATIONS = 32;
const HORIZON_ARC_STEP_DEGREES = 0.25;

interface Vector3 {
    x: number;
    y: number;
    z: number;
}

export interface HorizonClipper {
    isVisible(coordinate: GlobeCoordinate): boolean;
    intersection(start: GlobeCoordinate, end: GlobeCoordinate): GlobeCoordinate;
    horizonArc(from: GlobeCoordinate, to: GlobeCoordinate): GlobeCoordinate[];
}

// The camera and polygon vertices are represented in the same Earth-centred
// coordinate system. A surface point P is visible exactly when the segment from
// the camera C to P does not enter the sphere: dot(P, C) >= R^2.
export function createHorizonClipper(cameraLngLat: GlobeCoordinate, altitudeMeters: number): HorizonClipper | null {
    if (!isFiniteCoordinate(cameraLngLat) || !Number.isFinite(altitudeMeters) || altitudeMeters < 0) {
        return null;
    }

    const cameraDirection = toUnitVector(cameraLngLat);
    const cameraDistance = EARTH_RADIUS_METERS + altitudeMeters;
    const camera = scale(cameraDirection, cameraDistance);
    const visibilityThreshold = EARTH_RADIUS_METERS * EARTH_RADIUS_METERS;
    const visibilityTolerance = visibilityThreshold * HORIZON_EPSILON;
    const horizonRadius = Math.sqrt(Math.max(0, 1 - Math.pow(EARTH_RADIUS_METERS / cameraDistance, 2)));
    const horizonOffset = EARTH_RADIUS_METERS / cameraDistance;
    const basis = perpendicularBasis(cameraDirection);

    const visibilityValue = (coordinate: GlobeCoordinate) => dot(scale(toUnitVector(coordinate), EARTH_RADIUS_METERS), camera) - visibilityThreshold;
    const isVisible = (coordinate: GlobeCoordinate) => visibilityValue(coordinate) >= -visibilityTolerance;

    return {
        isVisible,
        intersection: (start, end) => findHorizonIntersection(start, end, visibilityValue),
        horizonArc: (from, to) => {
            if (horizonRadius < 1e-12) {
                return [to];
            }

            const fromAngle = horizonAngle(toUnitVector(from), cameraDirection, basis, horizonOffset, horizonRadius);
            const toAngle = horizonAngle(toUnitVector(to), cameraDirection, basis, horizonOffset, horizonRadius);
            let delta = toAngle - fromAngle;
            while (delta > Math.PI) delta -= 2 * Math.PI;
            while (delta <= -Math.PI) delta += 2 * Math.PI;

            const stepRadians = (HORIZON_ARC_STEP_DEGREES * Math.PI) / 180;
            const steps = Math.max(1, Math.ceil(Math.abs(delta) / stepRadians));
            const arc: GlobeCoordinate[] = [];
            for (let step = 1; step <= steps; step++) {
                const angle = fromAngle + (delta * step) / steps;
                const point = add(scale(cameraDirection, horizonOffset), add(scale(basis.u, horizonRadius * Math.cos(angle)), scale(basis.v, horizonRadius * Math.sin(angle))));
                arc.push(fromUnitVector(normalize(point)));
            }
            return arc;
        },
    };
}

// Clips a geodesic polygon ring against the camera-visible hemisphere. Each
// disconnected visible run becomes its own closed ring, with its open end
// closed along the true spherical horizon rather than a screen-space chord.
export function clipRingToHorizon(ring: GlobeCoordinate[], clipper: HorizonClipper): GlobeCoordinate[][] {
    const vertices = normalizeRing(ring);
    if (vertices.length < 3) {
        return [];
    }

    const visibility = vertices.map((coordinate) => clipper.isVisible(coordinate));
    if (visibility.every(Boolean)) {
        return [closeRing(vertices)];
    }
    if (visibility.every((value) => !value)) {
        return [];
    }

    const firstInvisible = visibility.findIndex((value) => !value);
    const paths: GlobeCoordinate[][] = [];
    let activePath: GlobeCoordinate[] | null = null;
    let pathStart: GlobeCoordinate | null = null;

    for (let offset = 0; offset < vertices.length; offset++) {
        const startIndex = (firstInvisible + offset) % vertices.length;
        const endIndex = (startIndex + 1) % vertices.length;
        const start = vertices[startIndex];
        const end = vertices[endIndex];
        const startVisible = visibility[startIndex];
        const endVisible = visibility[endIndex];

        if (!startVisible && endVisible) {
            pathStart = clipper.intersection(start, end);
            activePath = [pathStart, end];
        } else if (startVisible && endVisible) {
            activePath?.push(end);
        } else if (startVisible && !endVisible && activePath && pathStart) {
            const pathEnd = clipper.intersection(start, end);
            activePath.push(pathEnd, ...clipper.horizonArc(pathEnd, pathStart));
            const closed = closeRing(activePath);
            if (closed.length >= 4) {
                paths.push(closed);
            }
            activePath = null;
            pathStart = null;
        }
    }

    return paths;
}

function findHorizonIntersection(start: GlobeCoordinate, end: GlobeCoordinate, visibilityValue: (coordinate: GlobeCoordinate) => number): GlobeCoordinate {
    let low = start;
    let high = end;
    let lowValue = visibilityValue(low);

    for (let iteration = 0; iteration < INTERSECTION_ITERATIONS; iteration++) {
        const midpoint = interpolateGreatCircle(low, high, 0.5);
        const midpointValue = visibilityValue(midpoint);
        const lowVisible = lowValue >= 0;
        const midpointVisible = midpointValue >= 0;
        if (lowVisible === midpointVisible) {
            low = midpoint;
            lowValue = midpointValue;
        } else {
            high = midpoint;
        }
    }

    return interpolateGreatCircle(low, high, 0.5);
}

export function interpolateGreatCircle(start: GlobeCoordinate, end: GlobeCoordinate, t: number): GlobeCoordinate {
    const startVector = toUnitVector(start);
    const endVector = toUnitVector(end);
    const theta = Math.acos(clamp(dot(startVector, endVector), -1, 1));
    if (theta < 1e-12) {
        return [start[0], start[1]];
    }

    const sinTheta = Math.sin(theta);
    const result = add(scale(startVector, Math.sin((1 - t) * theta) / sinTheta), scale(endVector, Math.sin(t * theta) / sinTheta));
    return fromUnitVector(normalize(result));
}

function normalizeRing(ring: GlobeCoordinate[]): GlobeCoordinate[] {
    const valid = (ring || []).filter(isFiniteCoordinate).map((coordinate) => [coordinate[0], coordinate[1]] as GlobeCoordinate);
    if (valid.length > 1 && sameCoordinate(valid[0], valid[valid.length - 1])) {
        valid.pop();
    }
    return valid;
}

function closeRing(ring: GlobeCoordinate[]): GlobeCoordinate[] {
    if (!ring.length) return [];
    const result = ring.map((coordinate) => [coordinate[0], coordinate[1]] as GlobeCoordinate);
    if (!sameCoordinate(result[0], result[result.length - 1])) {
        result.push([result[0][0], result[0][1]]);
    }
    return result;
}

function toUnitVector([longitude, latitude]: GlobeCoordinate): Vector3 {
    const lng = (longitude * Math.PI) / 180;
    const lat = (latitude * Math.PI) / 180;
    const cosLat = Math.cos(lat);
    return { x: cosLat * Math.cos(lng), y: cosLat * Math.sin(lng), z: Math.sin(lat) };
}

function fromUnitVector(vector: Vector3): GlobeCoordinate {
    return [(Math.atan2(vector.y, vector.x) * 180) / Math.PI, (Math.asin(clamp(vector.z, -1, 1)) * 180) / Math.PI];
}

function perpendicularBasis(axis: Vector3): { u: Vector3; v: Vector3 } {
    const reference = Math.abs(axis.z) < 0.9 ? { x: 0, y: 0, z: 1 } : { x: 0, y: 1, z: 0 };
    const u = normalize(cross(reference, axis));
    return { u, v: normalize(cross(axis, u)) };
}

function horizonAngle(point: Vector3, axis: Vector3, basis: { u: Vector3; v: Vector3 }, offset: number, radius: number): number {
    const onCircle = scale(add(point, scale(axis, -offset)), 1 / radius);
    return Math.atan2(dot(onCircle, basis.v), dot(onCircle, basis.u));
}

function isFiniteCoordinate(coordinate: GlobeCoordinate): boolean {
    return Array.isArray(coordinate) && Number.isFinite(coordinate[0]) && Number.isFinite(coordinate[1]);
}

function sameCoordinate(a: GlobeCoordinate, b: GlobeCoordinate): boolean {
    return Math.abs(a[0] - b[0]) < 1e-10 && Math.abs(a[1] - b[1]) < 1e-10;
}

function dot(a: Vector3, b: Vector3): number {
    return a.x * b.x + a.y * b.y + a.z * b.z;
}

function cross(a: Vector3, b: Vector3): Vector3 {
    return { x: a.y * b.z - a.z * b.y, y: a.z * b.x - a.x * b.z, z: a.x * b.y - a.y * b.x };
}

function add(a: Vector3, b: Vector3): Vector3 {
    return { x: a.x + b.x, y: a.y + b.y, z: a.z + b.z };
}

function scale(vector: Vector3, amount: number): Vector3 {
    return { x: vector.x * amount, y: vector.y * amount, z: vector.z * amount };
}

function normalize(vector: Vector3): Vector3 {
    const length = Math.sqrt(dot(vector, vector));
    return length > 0 ? scale(vector, 1 / length) : { x: 0, y: 0, z: 1 };
}

function clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
}
